/* Remortgage Guide — full body content. Expanded to first-time-buyer-guide depth.
   Single source of truth: edit copy HERE. Remortgage.jsx and scripts/prerender.js
   both render from this module. Section shape mirrors ftb-guide.js:
   { id, h, intro?, paragraphs?[], bullets?[], items?[{h,b}] }. */
module.exports = [
  {
    id: "overview",
    h: "What remortgaging means",
    intro:
      "Remortgaging is the process of moving your existing mortgage onto a new deal — either staying with your current lender (a 'product transfer') or switching to a new lender (a full remortgage). It is one of the most common financial reviews UK homeowners carry out, usually every few years as fixed deals come to an end.",
    paragraphs: [
      "Unlike a house purchase, remortgaging doesn't involve buying or selling a property — you already own it. The aim is normally to secure a better interest rate, change your monthly payment, adjust your term, or release some of the equity you've built up.",
      "MortgagePath does not give regulated mortgage advice. This guide explains how remortgaging typically works in the UK so you can arrive at a broker or lender conversation already understanding the language and the timeline.",
    ],
  },
  {
    id: "why",
    h: "1. Why people remortgage",
    intro:
      "There are several common reasons UK homeowners look to remortgage. Understanding your own motivation helps you judge whether a simple product transfer is enough, or whether a full remortgage to a new lender is worth the extra paperwork.",
    bullets: [
      "Your current fixed or tracker deal is ending and you want to avoid the lender's Standard Variable Rate (SVR).",
      "Interest rates have moved and a new deal could reduce your monthly payment.",
      "Your property value or equity has increased, potentially moving you into a lower loan-to-value band with better pricing.",
      "You want to overpay, shorten your term, or change from interest-only to repayment.",
      "You want to release equity ('capital raising') for home improvements, debt consolidation or other purposes.",
      "You want to add or remove a borrower — for example after a relationship change.",
    ],
  },
  {
    id: "when",
    h: "2. When to start — timing your switch",
    intro:
      "Timing is the single most important part of a smooth remortgage. Start too late and you risk slipping onto the expensive SVR; start too early and you may face early repayment charges.",
    paragraphs: [
      "Most homeowners begin reviewing their options around 4–6 months before their current deal ends. Many lenders will let you secure a new product transfer rate up to six months ahead, then switch to it automatically when your current deal expires — protecting you if rates rise in the meantime.",
      "If you do nothing when your deal ends, you'll usually roll onto the lender's Standard Variable Rate, which is typically higher and can change at any time. Diarising your deal-end date is the easiest way to avoid paying more than you need to.",
    ],
    bullets: [
      "Find your current deal's end date and any early repayment charge window.",
      "Begin comparing options around 4–6 months before that date.",
      "Lock in a new rate early where possible — you can often switch lenders right up to completion if a better deal appears.",
    ],
  },
  {
    id: "transfer",
    h: "3. Product transfer vs full remortgage",
    intro:
      "The two main routes have very different effort levels. Understanding the trade-offs helps you decide which is right for you.",
    items: [
      {
        h: "Product transfer (stay with your lender)",
        b: "You move to a new deal with your existing lender. It's quick, usually involves little or no paperwork, often no new affordability check, no valuation and no legal work. The downside is you only see your current lender's products — which may not be the most competitive on the market.",
      },
      {
        h: "Full remortgage (switch lender)",
        b: "You move your mortgage to a new lender. This can unlock more competitive rates and let you change the loan amount or term, but it typically involves an affordability assessment, a credit check, a property valuation and conveyancing (legal work).",
      },
      {
        h: "How to choose",
        b: "If you simply want the lowest payment and your circumstances are unchanged, compare your lender's product-transfer offer against the wider market. If you want to borrow more, change the term significantly, or your current lender's rates are uncompetitive, a full remortgage is often worth the extra steps.",
      },
    ],
  },
  {
    id: "ltv",
    h: "4. Loan-to-value, equity and rate bands",
    intro:
      "Loan-to-value (LTV) is your outstanding mortgage as a percentage of your property's current value. As you repay capital and as property values rise, your LTV falls and your equity grows.",
    paragraphs: [
      "Lenders price mortgages in LTV bands — commonly 60%, 75%, 85% and 90%. Dropping into a lower band, even by a percentage point, can unlock a noticeably better rate. Before remortgaging, it's worth estimating your current property value realistically, because a higher valuation improves your LTV and your options.",
    ],
    bullets: [
      "Equity = current property value minus outstanding mortgage balance.",
      "Lower LTV (more equity) generally means access to more competitive products.",
      "If you're close to a band boundary, a small overpayment before remortgaging could tip you into a cheaper bracket.",
    ],
  },
  {
    id: "afford",
    h: "5. Affordability and credit checks",
    intro:
      "A product transfer with your existing lender usually skips a fresh affordability assessment. A full remortgage to a new lender almost always includes one.",
    paragraphs: [
      "The new lender will typically assess your income, regular commitments, dependants and outgoings, and apply a stress-tested interest rate to check you could still afford payments if rates rose. They will also normally run a hard credit search. If your income has fallen, your commitments have grown, or your credit profile has changed since your last application, this assessment matters — and is a key reason some homeowners stay with their current lender via a product transfer instead. Criteria vary by lender and applicant.",
    ],
  },
  {
    id: "erc",
    h: "6. Early Repayment Charges (ERCs)",
    intro:
      "An Early Repayment Charge is a fee your current lender may apply if you repay or switch your mortgage before your existing deal ends.",
    paragraphs: [
      "ERCs are most common on fixed-rate deals and are usually expressed as a percentage of the outstanding balance — often tapering down each year (for example 5% in year one, 4% in year two, and so on). On a large balance an ERC can run into thousands of pounds, so it can wipe out the savings from switching early.",
      "Always check your current mortgage offer or annual statement for the exact ERC amount and the date it ends before timing a switch. Sometimes waiting a few weeks for the ERC window to close saves far more than moving early.",
    ],
    bullets: [
      "ERCs typically apply during a fixed or discounted period and disappear afterwards.",
      "Most lenders allow penalty-free overpayments of up to 10% of the balance each year, even within an ERC period.",
      "Weigh any ERC against the total saving from the new deal before deciding to switch early.",
    ],
  },
  {
    id: "costs",
    h: "7. Costs and fees to expect",
    intro:
      "A product transfer is usually low- or no-cost. A full remortgage can carry several fees, though many can be added to the loan or covered by 'free legals' products.",
    bullets: [
      "Arrangement / product fee — often £0–£1,500; sometimes added to the loan (which then accrues interest).",
      "Valuation fee — frequently free on remortgage products, otherwise £100–£400.",
      "Legal / conveyancing fee — many remortgage products include 'free legals'; otherwise budget £300–£600.",
      "Early Repayment Charge — only if you switch before your current deal ends (see above).",
      "Exit / deeds-release fee — a small admin fee some lenders charge when you leave, often around £50–£100.",
      "Broker fee — some brokers are fee-free; others charge a flat fee or a percentage.",
    ],
  },
  {
    id: "process",
    h: "8. The remortgage process step by step",
    intro:
      "A full remortgage follows a predictable sequence. A product transfer is a much shorter version of the same idea.",
    items: [
      {
        h: "Review and compare",
        b: "Find your deal-end date and ERC window, estimate your property value and LTV, then compare your lender's transfer offer with the wider market.",
      },
      {
        h: "Apply",
        b: "Submit an application to the chosen lender. For a full remortgage this includes income and expenditure details, ID, and a hard credit search.",
      },
      {
        h: "Valuation",
        b: "The new lender values the property (often a desktop or drive-by valuation) to confirm the LTV and the amount they'll lend.",
      },
      {
        h: "Mortgage offer",
        b: "If approved, you receive a formal mortgage offer setting out the rate, term and any fees.",
      },
      {
        h: "Legal work",
        b: "A conveyancer handles the legal transfer between lenders — redeeming your old mortgage and registering the new one. With a product transfer, this step is skipped entirely.",
      },
      {
        h: "Completion",
        b: "The new lender pays off your old mortgage and your new deal begins. Your first new payment is usually due the following month.",
      },
    ],
  },
  {
    id: "raising",
    h: "9. Borrowing more (capital raising)",
    intro:
      "A remortgage can also be used to borrow more against your property — for example to fund home improvements or consolidate other debts.",
    paragraphs: [
      "Capital raising increases your mortgage balance and your LTV, and the lender will assess affordability for the larger amount. Consolidating short-term debt into a mortgage spreads it over a much longer period, which lowers the monthly cost but can increase the total interest paid over time and secures previously unsecured debt against your home. This is an area where regulated advice is particularly valuable.",
    ],
  },
  {
    id: "svr",
    h: "10. What happens if you do nothing",
    intro:
      "If your deal ends and you take no action, your lender will move you onto its Standard Variable Rate (SVR).",
    paragraphs: [
      "The SVR is set by the lender and can change at any time, independent of the Bank of England base rate. Because SVRs are often higher than the fixed or tracker rates available, many homeowners review their options before their existing deal ends. Whether changing product is suitable will depend on the rates, fees, early repayment charges and individual circumstances. There are no ERCs on the SVR, however, so it can occasionally suit someone planning to move or repay very soon.",
    ],
  },
  {
    id: "broker",
    h: "11. When speaking to a broker may help",
    intro:
      "A regulated mortgage broker can compare your existing lender's product-transfer offer against the whole market, sense-check ERC timing, and handle the paperwork.",
    bullets: [
      "They can model whether paying an ERC to switch early is still worth it overall.",
      "They can identify lenders that treat your income type (self-employed, bonus, rental) favourably.",
      "They can advise on capital raising and debt consolidation, which carry longer-term trade-offs.",
      "Brokers are regulated to give advice; MortgagePath is educational only and does not recommend products.",
    ],
    affiliate: {
      id: "broker",
      label: "Mortgage support",
      title: "Expert mortgage advice that won't cost you a penny",
      desc: "Whether you're buying your first home, moving house or looking for a new mortgage deal, L&C can help. Get expert mortgage advice, compare deals from across the market and receive support every step of the way.",
      href: "https://www.landc.co.uk/partners/mortgagepath-uk",
      cta: "Get mortgage help",
      disclosure: "Your home or property may be repossessed if you do not keep up repayments on your mortgage. MortgagePath commission disclosure: If you complete a mortgage through L&C after using our link, MortgagePath receives a commission from L&C. The amount is calculated as a percentage of the commission L&C receives from the lender. This does not increase what you pay.",
      external: true,
    },
  },
  {
    id: "how",
    h: "12. How MortgagePath helps",
    intro:
      "We don't sell mortgages and we don't give regulated advice. We help you understand the figures and the journey before you speak to a broker or lender.",
    bullets: [
      "Use the free Mortgage Snapshot to see your estimated equity, LTV and monthly repayment.",
      "Use the calculator to explore new rates, term changes and overpayment scenarios.",
      "Read the FAQs for plain-English answers to common remortgage questions.",
      "If you'd like regulated advice, you can follow our optional link to a qualified, regulated broker — entirely optional. You'll be leaving MortgagePath and dealing directly with the broker.",
    ],
  },
];
